﻿import {
	HTMLInputAutoCompleteAttribute,
	JSX,
	SyntheticEvent,
	useCallback
} from "react";
import {useControlId} from "../helpers/useControlId";
import Input, {HtmlInputType} from "../HtmlControls/Input";
import {MvvmTextControlProps} from "./MvvmControlProps";
import {useMvvmFieldSubscription} from "./MvvmDataProvider";
import {useMvvmStringInput} from "./useMvvmStringInput";

import {useRegisterValidation} from "./useRegisterValidation";
import {unwrapMvvmContext} from "./useMvvmTracking";

export interface MvvmInputProps<T extends object, TValue> {
	control: MvvmTextControlProps<T, TValue>;
	type: HtmlInputType;
	valueToString: (value: TValue | undefined) => string;
	valueFromString: (value: string | undefined) => TValue | undefined;
	updateOnChange: boolean;
	max?: number | string;
	min?: number | string;
	step?: number | "any";
	maxLength?: number;
	minLength?: number;
	autoComplete?: HTMLInputAutoCompleteAttribute;
	autoCapitalize?: "off" | "none" | "on" | "sentences" | "words" | "characters" | undefined | string & {};
	enterKeyHint?: "enter" | "done" | "go" | "next" | "previous" | "search" | "send" | undefined;
	inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search" | undefined;
	list?: string;
}

export default function MvvmInput<T extends object = never, TValue = never>(props: MvvmInputProps<T, TValue>): JSX.Element {
	const {
		control,
		type,
		valueToString,
		valueFromString,
		updateOnChange,
		max,
		min,
		step,
		maxLength,
		minLength,
		autoComplete,
		autoCapitalize,
		enterKeyHint,
		inputMode,
		list
	} = props;

	const {
		context: contextProp,
		property,
		id,
		label,
		hint,
		placeholder,
		column,
		controlPrefix,
		controlSuffix,
		hidden,
		disabled,
		inlineError,
		validation,
		transform,
		onChange,
		onBlur
	} = control;

	// Controls read the raw context; see unwrapMvvmContext.
	const context = unwrapMvvmContext(contextProp);
	const controlId = useControlId(id);
	const {value, handleChange, handleBlur} = useMvvmStringInput<T, TValue>(
		context, property, valueToString, valueFromString, updateOnChange, onChange, onBlur, transform
	);

	const onChangeInternal = useCallback(function (e: SyntheticEvent<HTMLInputElement>): void {
		handleChange(e.currentTarget.value);
	}, [handleChange]);

	useRegisterValidation(context, property, controlId, hidden, validation);
	useMvvmFieldSubscription(context, property);

	return <Input label={label}
	              name={property}
	              type={type}
	              value={value}
	              controlSuffix={controlSuffix}
	              controlPrefix={controlPrefix}
	              hint={hint}
	              hidden={hidden}
	              column={column}
	              id={id}
	              required={validation?.required != null}
	              error={context.getError(property)?.error}
	              inlineError={inlineError}
	              disabled={disabled}
	              placeholder={placeholder}
	              max={max}
	              min={min}
	              step={step}
	              maxLength={maxLength}
	              minLength={minLength}
	              autoFocus={control.autoFocus}
	              autoCapitalize={autoCapitalize}
	              autoComplete={autoComplete}
	              enterKeyHint={enterKeyHint}
	              inputMode={inputMode}
	              list={list}
	              onChange={onChangeInternal}
	              onBlur={handleBlur}
	/>;
};
